// Phase 7 money and operations rules for PopCart: pure, shared by the API (authoritative), the Ops
// console and the Partner Hub. Integer centavos only. Contract: docs/PHASE7-CONTRACT.md.

const MANILA_MS = 8 * 3600 * 1000;

/** Commission on a gross amount, rounded half up to the centavo. bps: 1500 = 15%. */
export function commission(grossCentavos, commissionBps) {
  const g = Math.max(0, Math.trunc(grossCentavos));
  const b = Math.max(0, Math.trunc(commissionBps));
  return Math.floor((g * b + 5000) / 10000);
}

/**
 * A partner's statement for a period.
 * @param {{ subtotals: number[], commissionBps: number, adjustments?: number[] }} p
 *   subtotals: delivered shop_orders' subtotals; adjustments: signed amounts against the shop
 *   (negative = the shop owes it, e.g. a shop_absorbed catch-weight overrun).
 * @returns {{ ordersCount: number, grossCentavos: number, commissionCentavos: number, adjustmentsCentavos: number, netCentavos: number }}
 */
export function shopStatement({ subtotals, commissionBps, adjustments = [] }) {
  const grossCentavos = subtotals.reduce((s, x) => s + Math.trunc(x), 0);
  const commissionCentavos = commission(grossCentavos, commissionBps);
  const adjustmentsCentavos = adjustments.reduce((s, x) => s + Math.trunc(x), 0);
  return {
    ordersCount: subtotals.length,
    grossCentavos,
    commissionCentavos,
    adjustmentsCentavos,
    netCentavos: grossCentavos - commissionCentavos + adjustmentsCentavos,
  };
}

/** Cash a rider still holds: collected − remitted (negative = remitted more than collected; Ops sees it). */
export function cashOnHand(collectedCentavos, remittedCentavos) {
  return Math.trunc(collectedCentavos) - Math.trunc(remittedCentavos);
}

/** Over the rider's cash limit? (limit 0 = no limit). */
export function overCashLimit(onHandCentavos, cashLimitCentavos) {
  return cashLimitCentavos > 0 && onHandCentavos > cashLimitCentavos;
}

/**
 * How much more of an order can be refunded to an e-wallet or card.
 * @param {{ paidCentavos: number, refunds: { amountCentavos: number, status: 'pending'|'done'|'failed' }[] }} p
 *   refunds: existing refund rows, amounts as stored (negative = money back to the customer).
 */
export function refundable({ paidCentavos, refunds }) {
  const held = refunds.filter((r) => r.status !== 'failed').reduce((s, r) => s + Math.abs(Math.trunc(r.amountCentavos)), 0);
  return Math.max(0, Math.trunc(paidCentavos) - held);
}

/** Validate a manual refund amount. → null when fine, else a reason code. */
export function refundProblem(amountCentavos, refundableCentavos) {
  if (!Number.isInteger(amountCentavos) || amountCentavos <= 0) return 'amount_invalid';
  if (amountCentavos > refundableCentavos) return 'over_refundable';
  return null;
}

/** The Manila calendar day of an instant, as YYYY-MM-DD (reports bucket by it). */
export function manilaDay(at) {
  return new Date(new Date(at).getTime() + MANILA_MS).toISOString().slice(0, 10);
}

/** [start, end) instants for Manila days from..to inclusive (YYYY-MM-DD). */
export function manilaRange(from, to) {
  const start = new Date(Date.parse(`${from}T00:00:00Z`) - MANILA_MS);
  const end = new Date(Date.parse(`${to}T00:00:00Z`) - MANILA_MS + 86400000);
  return { start, end };
}

/** Every Manila day from..to inclusive, so report series have no gaps. */
export function manilaDays(from, to) {
  const out = [];
  for (let t = Date.parse(`${from}T00:00:00Z`), e = Date.parse(`${to}T00:00:00Z`); t <= e; t += 86400000) {
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  return out;
}

/**
 * Is a shop taking orders right now (hours aside)?
 * @param {{ acceptingOrders: boolean, pausedUntil: string|Date|null, now: Date }} p
 * @returns {{ taking: boolean, reason: 'off'|'paused'|null, until: string|null }}
 */
export function pauseState({ acceptingOrders, pausedUntil, now }) {
  if (!acceptingOrders) return { taking: false, reason: 'off', until: null };
  if (pausedUntil && new Date(pausedUntil).getTime() > now.getTime()) {
    return { taking: false, reason: 'paused', until: new Date(pausedUntil).toISOString() };
  }
  return { taking: true, reason: null, until: null };
}

/** Does a new product from this actor wait for Ops review? Platform admins and first-party shops never wait. */
export function needsReview({ reviewNewProducts, actorIsPlatformAdmin, shopIsFirstParty }) {
  return !!reviewNewProducts && !actorIsPlatformAdmin && !shopIsFirstParty;
}

/** A price change bigger than maxPct (either way) goes back to review. maxPct 0 = no limit. */
export function priceJumpTooBig(oldCentavos, newCentavos, maxPct) {
  if (!maxPct || !oldCentavos) return false;
  return Math.abs(newCentavos - oldCentavos) * 100 > oldCentavos * maxPct;
}
